import {
  Autocomplete,
  AutocompleteChangeReason,
  Box,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { whereFilterType } from "src/types/types";
import { getIconFromType } from "src/views/datasets/components/DatasetTypeChip";
import { PopperProps } from "@mui/material/Popper";

const CustomPopper = (props: PopperProps) => {
  return (
    <Popper
      key={props.key}
      {...props}
      placement="bottom-start"
      sx={{ width: "fit-content" }}
    />
  );
};

type Value = {
  label: string;
  value: string;
};

type ComponentType = "default" | "datasetType";

type FilterSelectProps = {
  label: string;
  column: string;
  whereFilters: whereFilterType[];
  setWhereFilters: React.Dispatch<React.SetStateAction<whereFilterType[]>>;
  options: Value[];
  type?: ComponentType;
  /**
   * When set, options are server-fetched from the typed text: this reports
   * each keystroke and MUI's client-side option filtering is disabled (the
   * server may match on fields not present in the label, e.g. username vs
   * display name).
   */
  onInputChange?: (value: string) => void;
};

const EqualFilterSelect = ({
  label,
  column,
  whereFilters,
  setWhereFilters,
  options = [],
  type = "default",
  onInputChange,
}: FilterSelectProps) => {
  // optional inital filter value is set in the whereFilters
  const getFilterValue = () => {
    const filter = whereFilters.find((f) => f.filter_on.name === column);
    return filter ? filter.filter_value : null;
  };

  const [value, setValue] = useState<any>(getFilterValue());

  const handleFilterChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValue: any | any[],
    reason: AutocompleteChangeReason,
  ) => {
    if (reason === "selectOption") {
      setValue(newValue);
      setWhereFilters((prevWhereFilters) => {
        const updatedFilters = [...prevWhereFilters];
        const filterIndex = updatedFilters.findIndex(
          (filter) => filter.filter_on.name === column,
        );

        if (newValue.value === "") {
          // Remove filter if value is empty
          if (filterIndex !== -1) {
            updatedFilters.splice(filterIndex, 1);
          }
        } else {
          if (filterIndex !== -1) {
            // Update existing filter
            updatedFilters[filterIndex] = {
              ...updatedFilters[filterIndex],
              filter_value: newValue.value,
            };
          } else {
            // Add new filter
            updatedFilters.push({
              filter_on: { group: "dataset", name: column, column: column },
              comparison_operator: "eq",
              value_datatype: "string",
              filter_value: newValue.value,
            });
          }
        }

        return updatedFilters;
      });
    }

    if (reason === "clear") {
      handleClearFilter();
    }
  };

  const handleClearFilter = () => {
    setValue(null);
    setWhereFilters((prevWhereFilters) =>
      prevWhereFilters.filter((filter) => filter.filter_on.name !== column),
    );
  };

  return (
    <Box sx={{ minWidth: 150 }}>
      <Paper elevation={0}>
        <Autocomplete
          size="small"
          getOptionLabel={(option) => option.label}
          value={value}
          onChange={handleFilterChange}
          options={options}
          {...(onInputChange && {
            onInputChange: (
              _event: React.SyntheticEvent,
              inputValue: string,
              reason: string,
            ) => {
              if (reason === "input") onInputChange(inputValue);
            },
            filterOptions: (opts: Value[]) => opts,
          })}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              InputProps={{
                ...params.InputProps,
              }}
            />
          )}
          PopperComponent={CustomPopper}
          componentsProps={{ popper: { style: { width: "fit-content" } } }}
          renderOption={(props: any, option) => {
            const { key, ...restProps } = props;
            return (
              <li key={key} {...restProps}>
                <Stack flexDirection="row" gap={2.5} alignItems="center">
                  {type === "datasetType" &&
                    getIconFromType(option.value, true, 20)}
                  <Typography variant="body1">{option.label}</Typography>
                </Stack>
              </li>
            );
          }}
        />
      </Paper>
    </Box>
  );
};

export default EqualFilterSelect;
